import React from 'react';

/**
 * WI-F1 — Hard-failure screen (ADO #38529).
 *
 * Standalone screen shown when the runtime tenant config cannot be resolved on
 * boot — an explicit message instead of a blank app.
 *
 * Intentionally self-contained: it must render BEFORE (or instead of) any
 * runtime config, MSAL instance, theme provider or axios client exists, so it
 * deliberately avoids `@celito.clients/*` runtime dependencies and uses inline
 * styles only. WI-F4 will mount this in the boot sequence for the
 * `tenant-not-found` and `config-unavailable` outcomes of `fetchTenantConfig`.
 */

export type TenantErrorVariant = 'tenant-not-found' | 'config-unavailable';

export interface TenantErrorViewProps {
  /**
   * Which failure to present. Maps 1:1 to the non-success
   * `TenantConfigResult` statuses. Defaults to `config-unavailable`.
   */
  variant?: TenantErrorVariant;
}

const COPY: Record<TenantErrorVariant, { title: string; message: string }> = {
  'tenant-not-found': {
    title: 'Tenant not found',
    message:
      'We could not find an active workspace for this address. Please check the URL, or contact your administrator if you believe this is a mistake.',
  },
  'config-unavailable': {
    title: 'Configuration unavailable',
    message:
      'We were unable to load the configuration for this workspace. Please try again in a few moments, or contact your administrator if the problem persists.',
  },
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '2rem',
  boxSizing: 'border-box',
  textAlign: 'center',
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  color: '#1f2937',
  backgroundColor: '#f9fafb',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '32rem',
  width: '100%',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  margin: '0 0 0.75rem',
};

const messageStyle: React.CSSProperties = {
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: 0,
  color: '#4b5563',
};

const TenantErrorView = ({
  variant = 'config-unavailable',
}: TenantErrorViewProps): JSX.Element => {
  const { title, message } = COPY[variant] ?? COPY['config-unavailable'];

  return (
    <div style={containerStyle} role="alert" data-testid="tenant-error-screen">
      <div style={cardStyle}>
        <h1 style={titleStyle}>{title}</h1>
        <p style={messageStyle}>{message}</p>
      </div>
    </div>
  );
};

export default TenantErrorView;
